import React from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Scale, ShieldCheck, TrendingUp, Globe, Users, Briefcase } from "lucide-react";
const values = [{
  title: "Strategic Integrity",
  description: "Unwavering commitment to transparent, high-trust capital deployment. We operate with the rigor of a global institution, ensuring every decision is backed by ethical clarity.",
  icon: ShieldCheck
}, {
  title: "Execution Mastery",
  description: "Precision in underwriting and speed in closing. We eliminate ambiguity to provide certainty of execution for complex real estate and business transactions.",
  icon: TrendingUp
}, {
  title: "Institutional Discipline",
  description: "Rigorous risk management aligned with global standards. Our structured approach mirrors the operational excellence of top-tier asset managers.",
  icon: Scale
}, {
  title: "Capital Resilience",
  description: "Building enduring value through multi-layered funding strategies. We design capital structures that withstand market volatility and drive long-term growth.",
  icon: Briefcase
}, {
  title: "Global Perspective",
  description: "Leveraging international capital networks to fuel local growth. Our reach extends beyond traditional lending, tapping into diverse liquidity pools.",
  icon: Globe
}, {
  title: "Client Alignment",
  description: "Structuring deals that prioritize the long-term success of our partners. We view every transaction as the foundation for a lasting capital relationship.",
  icon: Users
}];
export default function LeadershipPhilosophy() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="leadership-philosophy" ref={ref} className={cn("dark bg-background text-foreground py-24 relative overflow-hidden", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10", "transition-all duration-1000 ease-out")}>
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-px h-full bg-border/10" />
        <div className="absolute top-0 right-1/4 w-px h-full bg-border/10" />
        <div className="absolute top-1/2 left-0 w-full h-px bg-border/10" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-medium tracking-tight mb-6 text-foreground">
            Our Philosophy
          </h2>
          <p className="text-lg text-muted-foreground font-sans max-w-2xl mx-auto leading-relaxed">DynEstate operates at the intersection of institutional capital and strategic growth. Our core values define our approach to every transaction.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {values.map((item, i) => <div key={i} data-index={i} className="group">
              <HoverCard openDelay={0} closeDelay={100}>
                <HoverCardTrigger className="block h-full cursor-default">
                  <Card className="h-full bg-card/50 border border-border/40 hover:border-primary/50 hover:bg-card transition-all duration-500 rounded-sm group-hover:shadow-lg">
                    <CardHeader className="pb-4 pt-8 px-8">
                      <div className="mb-6 text-muted-foreground group-hover:text-primary transition-colors duration-300">
                        <item.icon strokeWidth={1} className="w-8 h-8" />
                      </div>
                      <CardTitle className="text-2xl font-serif font-normal tracking-wide text-foreground group-hover:translate-x-1 transition-transform duration-300">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-8 pb-8">
                      <div className="h-px w-12 bg-border group-hover:w-full group-hover:bg-primary/30 transition-all duration-500 ease-out" />
                    </CardContent>
                  </Card>
                </HoverCardTrigger>
                <HoverCardContent side="top" align="start" className="w-80 bg-popover border-border/50 text-popover-foreground p-6 rounded-sm shadow-xl backdrop-blur-xl">
                  <div className="space-y-3">
                    <h4 className="font-serif text-lg font-medium text-foreground">{item.title}</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>)}
        </div>
      </div>
    </section>;
}